'use client'

import { useMemo, useRef, useState } from 'react'

import { DotsThreeOutlineIcon } from '@phosphor-icons/react'
import { ChevronRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { isAutoDirectorModel } from '@/components/business/prompt/controls/director/director-data'
import { useAppContext } from '@/components/providers/app-provider'
import { useUser } from '@/components/providers/user-provider'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ModelConfigItem } from '@/lib/api/models/config'
import { findBlockedModel } from '@/lib/config/blocked-models'
import { filterVisibleModelsForUser } from '@/lib/config/hidden-models'
import { getDefaultGenerationMode, isRm35FastModel, usesDualMode } from '@/lib/config/model-dual-mode'
import { presetState, setSelectedPreset, getStoredAppliedParams, restorePendingPreset } from '@/lib/preset-state'
import { hasSeenRm32dIntro } from '@/lib/rm32d-intro-storage'
import { cn } from '@/lib/utils'
import { useImagesStore, useModelStore, useSubmitStore } from '@/stores'

export type ModelMenuListProps = {
  children: React.ReactNode
}

export const ModelMenuList = ({ children }: ModelMenuListProps) => {
  const [open, setOpen] = useState(false)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="z-popover w-max p-1.5">
        <ModelList onSelect={() => setOpen(false)} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export type ModelListProps = {
  onSelect?: () => void
}

const RM35_FAST_OFF_TAG = {
  name: '20% OFF',
  color: '#FFFFFF',
  bgColor: '#FF3466'
}

const RM35_FAST_SCENE_ID = '65'

const EXCLUDED_MEMBER_LEVELS = new Set([
  'free',
  'ultra',
  'ultrapro',
  'agentstandard',
  'real2utral',
  'real2ultrapro'
])

const normalizeMemberKey = (value: string) => value.replace(/[\s_-]/g, '').toLowerCase()

const isExcludedMembership = (rechargeType: string, level: string, ultraMax: boolean) => {
  if (ultraMax) return false
  const keys = [rechargeType, level].map(normalizeMemberKey).filter(Boolean)
  if (keys.some(key => key.includes('ultramax'))) return false
  return keys.some(key => EXCLUDED_MEMBER_LEVELS.has(key))
}

const isRm35FastListItem = (item: ModelConfigItem) =>
  isRm35FastModel(item) || String(item.sceneId) === RM35_FAST_SCENE_ID

const shouldShowRm35FastOffTag = (
  item: ModelConfigItem,
  rechargeType: string,
  level: string,
  ultraMax: boolean
) => isRm35FastListItem(item) && !isExcludedMembership(rechargeType, level, ultraMax)

/** 单个模型项组件 */
const ModelItem = ({
  item,
  isSelected,
  isMember,
  rechargeType,
  level,
  ultraMax,
  onClick
}: {
  item: ModelConfigItem
  isSelected: boolean
  isMember: boolean
  rechargeType: string
  level: string
  ultraMax: boolean
  onClick: () => void
}) => {
  const visibleTags = useMemo(() => {
    const tags = (item.tags ?? []).filter(tag => {
      if (tag.visibility === 'all') return true
      if (!tag.visibility) return true
      if (tag.visibility === 'member') return isMember
      if (tag.visibility === 'non-member') return !isMember
      return false
    })
    if (
      shouldShowRm35FastOffTag(item, rechargeType, level, ultraMax) &&
      !tags.some(tag => tag.name === RM35_FAST_OFF_TAG.name)
    ) {
      return [...tags, RM35_FAST_OFF_TAG]
    }
    return tags
  }, [item, isMember, rechargeType, level, ultraMax])

  return (
    <div
      className={cn(
        'flex cursor-pointer gap-2 rounded-md p-3 hover:bg-light/10',
        isSelected && 'bg-light/10'
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        {item.icon && (
          <span
            className="size-4 shrink-0 [&>svg]:size-full"
            dangerouslySetInnerHTML={{ __html: item.icon }}
          />
        )}
        <div className="flex items-center gap-1.5">
          <span className={cn('text-sm font-medium')}>{item.name}</span>
          {visibleTags.length > 0 && (
            <div className="ml-2 flex shrink-0 items-center gap-1">
              {visibleTags.map((tag, index) => (
                <Badge
                  key={index}
                  className="h-5.5"
                  style={{ color: tag.color, backgroundColor: tag.bgColor }}
                >
                  {tag.name}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const ModelList = ({ onSelect }: ModelListProps) => {
  const t = useTranslations('common')
  const tRoot = useTranslations()
  const { isMobile } = useAppContext()
  const { subscription, user } = useUser()
  const { setSelectedModel, modelConfigs, getEffectiveModelId } = useModelStore()
  // 使用生效的模型 ID（优先锁定模型）用于高亮显示
  const selectedModelId = getEffectiveModelId()
  // 更多模型弹窗状态
  const [moreModelsOpen, setMoreModelsOpen] = useState(false)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // 从 store 获取模型配置；新用户（id > 4090381）隐藏 RM 3.2 Remix / RM 2.6 Remix
  const models = useMemo(
    () => filterVisibleModelsForUser(modelConfigs, user?.id),
    [modelConfigs, user?.id]
  )

  // 分离正常显示的模型和折叠的模型
  const { visibleModels, collapsedModels } = useMemo(() => {
    // 移动端不做折叠处理，直接返回所有模型
    if (isMobile) {
      return { visibleModels: models, collapsedModels: [] }
    }
    return {
      visibleModels: models.filter(m => !m.defaultCollapsed),
      collapsedModels: models.filter(m => m.defaultCollapsed)
    }
  }, [models, isMobile])

  const handleSelectModel = (model: ModelConfigItem) => {
    if (model.memberOnly && !subscription.isMember) {
      subscription.setUpgradeOpen(true)
      onSelect?.()
      return
    }
    // Coming Soon 等拦截模型：允许选中，同时提示 toast（提交时仍会被 BLOCKED_MODELS 拦截）
    const blocked = findBlockedModel(model)
    if (blocked) {
      toast.error(tRoot(blocked.toastKey))
    }
    setSelectedModel(model)
    // 双模式模型：按 admin defaultGenerationMode 切换 Standard / Reference
    if (usesDualMode(model)) {
      useImagesStore
        .getState()
        .setReferenceMode(getDefaultGenerationMode(model) === 'reference')
    } else {
      useImagesStore.getState().setReferenceMode(false)
    }
    if (isAutoDirectorModel(model) && !hasSeenRm32dIntro(user?.id)) {
      useSubmitStore.getState().showRm32dIntro()
    }
    const templateParams = getStoredAppliedParams()
    console.log('[ModelList] handleSelectModel:', {
      modelSceneId: model.sceneId,
      templateModelId: templateParams?.modelId,
      isMatch: templateParams?.modelId === model.sceneId,
      hasSelectedPreset: !!presetState.selectedPreset,
      hasPendingPreset: !!presetState.pendingPreset
    })
    if (templateParams?.modelId === model.sceneId) {
      // 模型与模板原始模型相同，尝试恢复模板选中状态
      const restored = restorePendingPreset()
      console.log('[ModelList] restorePendingPreset result:', restored)
    } else if (presetState.selectedPreset) {
      // 模型与模板原始模型不同，清除模板选中状态（但保留 appliedParams 用于后续恢复判断）
      setSelectedPreset(null)
    }
    setMoreModelsOpen(false)
    onSelect?.()
  }

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    hoverTimeoutRef.current = setTimeout(() => setMoreModelsOpen(true), 100)
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    hoverTimeoutRef.current = setTimeout(() => setMoreModelsOpen(false), 150)
  }

  return (
    <ScrollArea className="flex max-h-[min(60dvh,var(--popover-content-available-height,60dvh))] flex-col gap-0.5">
      {visibleModels.map(item => (
        <div key={item.sceneId} className="flex flex-col gap-0.5" data-id={item.sceneId}>
          <ModelItem
            isMember={subscription.isMember}
            isSelected={selectedModelId === item.sceneId}
            item={item}
            level={String(subscription.level ?? '')}
            rechargeType={subscription.rechargeType}
            ultraMax={subscription.ultraMax}
            onClick={() => handleSelectModel(item)}
          />
        </div>
      ))}

      {/* 折叠的模型项 - 仅桌面端显示 */}
      {collapsedModels.length > 0 && (
        <Popover open={moreModelsOpen} onOpenChange={setMoreModelsOpen}>
          <PopoverTrigger asChild>
            <div
              className="flex cursor-pointer items-center justify-between gap-2 rounded-md p-3 hover:bg-light/10"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="flex items-center gap-2">
                <DotsThreeOutlineIcon className="size-4" weight="fill" />
                <span className="text-sm font-medium">
                  {t('moreModels', { count: collapsedModels.length })}
                </span>
              </div>
              <ChevronRight className="size-4" />
            </div>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="z-popover w-auto bg-light-600/95 p-1.5 [box-shadow:0_20px_30px_0_rgba(14,_14,_15,_0.50)] outline-hidden backdrop-blur-xl"
            side="right"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <ScrollArea className="flex max-h-[40dvh] flex-col gap-0.5">
              {collapsedModels.map(item => (
                <div key={item.sceneId} className="flex flex-col gap-0.5" data-id={item.sceneId}>
                  <ModelItem
                    isMember={subscription.isMember}
                    isSelected={selectedModelId === item.sceneId}
                    item={item}
                    level={String(subscription.level ?? '')}
                    rechargeType={subscription.rechargeType}
                    ultraMax={subscription.ultraMax}
                    onClick={() => handleSelectModel(item)}
                  />
                </div>
              ))}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      )}
    </ScrollArea>
  )
}
